import { useDateTimeFormat } from '@core/shared';
import { WebinarDataWithStatus } from '@core/swagger-client';
import { CalendarAlt, Clock, DescriptionItem, Heading, PlannedDurationIcon, Star } from '@core/ui';
import { Link } from '@online-webinar/ui-frontend';
import { DateTime, Duration } from 'luxon';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';

export const WebinarsListUpNext = ({ isAuthenticated, webinar }: { isAuthenticated: boolean; webinar: WebinarDataWithStatus }) => {
    const { t } = useTranslation();
    // The title as hidden text after the visible text, so the links are not just "Details, Details, …"
    const linkContext = <span className="sr-only">{t('Generic.Link.Context', { title: webinar.title })}</span>;
    const { dateFormat, dateWeekday, time } = useDateTimeFormat(webinar.broadcastDateTime, {
        dateFormat: 'yyyy-LL-dd',
        dateWeekday: DateTime.DATE_MED_WITH_WEEKDAY,
        time: DateTime.TIME_24_SIMPLE
    });
    const plannedDuration = Duration.fromISOTime(webinar.plannedDuration);

    return (
        <article className="w-full bg-white rounded-webinarsub grid sm:grid-cols-[1fr_2fr] shadow-4xl">
            <div className="relative bg-accent rounded-t-webinarsub sm:rounded-webinarsub -mb-4 sm:mb-0 px-4 pt-4 pb-8 sm:pt-5 sm:pb-4 md:px-6 sm:after:absolute sm:after:-right-3 sm:after:top-0 sm:after:size-6 sm:after:bg-inherit">
                <Heading variant="h2" classNames="text-primary uppercase">
                    {t('Schedule.UpNext')}
                </Heading>
            </div>
            <div className="relative min-w-0 rounded-webinarsub">
                {webinar.assets.bannerImage && (
                    <div className="relative bg-gray-200 rounded-webinarsub overflow-hidden">
                        <img src={webinar.assets.bannerImage.imageUrl} className="w-full" alt="" />
                    </div>
                )}
                <div className="p-4 sm:pt-6 md:p-6">
                    <header className="relative">
                        <Heading variant="h3" classNames="mb-2">
                            {webinar.title}
                        </Heading>
                        <dl className="flex flex-wrap gap-y-2 gap-x-4 sm:gap-x-6 mb-4">
                            <DescriptionItem
                                classNames="flex items-start whitespace-nowrap"
                                termClassNames="flex flex-none"
                                icon={<CalendarAlt classNames="w-6 me-1.5 stroke-current" />}
                                label={t('WebinarDetails.Label.Date')}
                                hideLabel
                            >
                                <time dateTime={dateFormat}>{dateWeekday}</time>
                            </DescriptionItem>
                            <DescriptionItem
                                classNames="flex items-start whitespace-nowrap"
                                termClassNames="flex flex-none"
                                icon={<Clock classNames="w-6 me-1.5 stroke-current" />}
                                label={t('WebinarDetails.Label.Time')}
                                hideLabel
                            >
                                <time dateTime={time}>{time}</time>
                            </DescriptionItem>
                            <DescriptionItem
                                classNames="flex items-start whitespace-nowrap"
                                termClassNames="flex flex-none"
                                icon={<PlannedDurationIcon classNames="w-6 me-1.5 stroke-current" />}
                                label={t('WebinarDetails.Label.Duration')}
                                hideLabel
                            >
                                <time dateTime={plannedDuration.toFormat("h'h' m'm'")}>{plannedDuration.toFormat('h:mm')}</time>
                            </DescriptionItem>
                        </dl>
                        {isAuthenticated && (
                            <div className="absolute -top-9 -left-2 sm:-top-1 sm:-left-10 md:-left-11 bg-white rounded-webinarsub p-2">
                                <Star
                                    classNames={`w-6 ${webinar.isInterested ? 'fill-primary' : 'fill-none'} stroke-primary`}
                                    label={webinar.isInterested ? t('Generic.Interested') : t('Generic.NotInterested')}
                                />
                            </div>
                        )}
                    </header>
                    {webinar.description ? (
                        <div className="line-clamp-2 mb-4 sm:mb-6">
                            <Markdown>{webinar.description}</Markdown>
                        </div>
                    ) : (
                        ''
                    )}

                    <div className="flex flex-col xs:flex-row gap-4 mt-2 md:mt-0">
                        {webinar.assets.profileImage && (
                            <img
                                src={webinar.assets.profileImage.imageUrl}
                                className="size-12 xl:size-14 rounded-webinarsub"
                                alt={webinar.assets.profileImage.altText ?? ''}
                            />
                        )}
                        {/*TODO: add aria-labels specifying the title of the webinar*/}
                        <Link variant="primary-outline" classNames="xs:ms-auto" to="/$webinarId" params={{ webinarId: webinar.webinarId }}>
                            {t('Generic.Button.Details')}
                            {linkContext}
                        </Link>
                        {webinar.canWatch && webinar.isOnDemand && (
                            <Link variant="primary" to="/$webinarId/on-demand" params={{ webinarId: webinar.webinarId }}>
                                {t('Generic.Button.Watch')}
                                {linkContext}
                            </Link>
                        )}
                        {webinar.canWatch && !webinar.isOnDemand && (
                            <Link variant="primary" to="/$webinarId/watch" params={{ webinarId: webinar.webinarId }}>
                                {t('Generic.Button.Watch')}
                                {linkContext}
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </article>
    );
};
